import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import * as STS from 'aws-sdk/clients/sts';
import * as AWS from 'aws-sdk/global';
import { EnvironmentLoader } from '../../../environments/utils/environment-loader.util';
import { AuthService } from '../../core/auth/auth.service';
import { CognitoCallback, CognitoUtil, LoggedInCallback } from './cognito.service';
import { UserCreateService } from './user-create.service';
import { UserProfileService } from './user-profile.service';

const AmazonCognitoIdentity = require('amazon-cognito-auth-js');

@Injectable()
export class UserSigninService {

    private onLoginSuccess = (callback: CognitoCallback, session: CognitoUserSession) => {
        // console.log('In authenticateUser onSuccess callback');
        AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(session.getIdToken().getJwtToken());

        // So, when CognitoIdentity authenticates a user, it doesn't actually hand us the IdentityID,
        // used by many of our other handlers. This is handled by some sly underhanded calls to AWS Cognito
        // API's by the SDK itself, automatically when the first AWS SDK request is made that requires our
        // security credentials. The identity is then injected directly into the credentials object.
        // If the first SDK call we make wants to use our IdentityID, we have a
        // chicken and egg problem on our hands. We resolve this problem by "priming" the AWS SDK by calling a
        // very innocuous API call that forces this behavior.
        const clientParams: any = {};
        if (EnvironmentLoader.environment.sts_endpoint) clientParams.endpoint = EnvironmentLoader.environment.sts_endpoint;
        const sts = new STS(clientParams);
        sts.getCallerIdentity({}, function (err, data) {
            // console.log('UserLoginService: Successfully set the AWS credentials 2', session);
            callback.cognitoCallback(null, session);
        });
    };

    private onLoginError = (callback: CognitoCallback, err) => {
        callback.cognitoCallback(err.message, null);
    };

    constructor(
        public cognitoUtil: CognitoUtil,
        public router: Router,
        public userCreateService: UserCreateService,
        public authService: AuthService,
    ) {
        // console.log(environment);
    }

    logout() {
        // console.log('UserLoginService: Logging out');
        if (this.cognitoUtil.getCurrentUser()) {
            this.cognitoUtil.getCurrentUser().signOut();
            const auth = this.initCognito();
            auth.signOut();
            localStorage.clear();
            // this.router.navigate(['/login']);
        } else {
            window.location.href = EnvironmentLoader.environment.callbackURL;
        }

    }

    idleLogout() {
        this.cognitoUtil.getCurrentUser().signOut();
        const auth = this.initCognito();
        auth.signOut();
    }

    async isAuthenticated(callback: LoggedInCallback) {
        if (callback == null) throw new Error(('UserLoginService: Callback in isAuthenticated() cannot be null'));
        const cognitoUser = this.cognitoUtil.getCurrentUser();

        if (cognitoUser != null) {
            let sessionObject;

            await cognitoUser.getSession((err, session)=> {
                if (err) {
                    // console.log('UserLoginService: Couldn\'t get the session: ' + err, err.stack);
                    callback.isLoggedIn(err, false);
                } else {
                    sessionObject = session;
                    this.authService.accessToken = session.getAccessToken().getJwtToken();
                }
            });

            // Refresh session every 50 mins
            setInterval(async () => {
                await cognitoUser.refreshSession(sessionObject.getRefreshToken(), (refreshErr, refreshSession) => {
                    if (refreshErr) throw refreshErr;
                    else {
                        AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(refreshSession.getIdToken().getJwtToken());
                        this.userCreateService.userCreate(cognitoUser['username']).subscribe(res => {
                            if (res['success']) {
                                // get current user's profile and assign current user's full name by the signed in user's username);
                            }
                        });
                    }
                });
            }, 3000000);
            callback.isLoggedIn('', sessionObject.isValid());
        } else {
            callback.isLoggedIn('Can\'t retrieve the CurrentUser', false);
        }

    }

    async setToken(cognitoUser: CognitoUser) {
        return new Promise<void>((resolve, reject) => {
            cognitoUser.getSession((err, session) => {
                if(!err) this.authService.accessToken = session.getAccessToken().getJwtToken();
                resolve();
            });
        });
    }

    initCognito() {
        const authData = {
            ClientId: EnvironmentLoader.environment.clientId, // Your client id here
            AppWebDomain: EnvironmentLoader.environment.appWebDomain,
            TokenScopesArray: ['profile', 'email', 'openid', 'aws.cognito.signin.user.admin', 'phone'],
            RedirectUriSignIn: EnvironmentLoader.environment.callbackURL,
            RedirectUriSignOut: EnvironmentLoader.environment.signoutURL,
            IdentityProvider: EnvironmentLoader.environment.provider,
            UserPoolId: EnvironmentLoader.environment.userPoolId,
            AdvancedSecurityDataCollectionFlag: 'False'
        };

        const auth = new AmazonCognitoIdentity.CognitoAuth(authData);
        auth.userhandler = {
            onSuccess: result => {
                this.router.navigate(['/home-page']);
            },
            onFailure: function (err) {
                this.errorMessage = err;
            }
        };
        // auth.useCodeGrantFlow();
        return auth;
    }
}
